import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { Level } from './crmApi';

// ── Chart pieces shared by the CRM dashboard and home pages ──────
// Series colours are the validated light-surface slots (blue = leads,
// orange = converted); a lighter step of the blue marks earlier periods.
// Text stays in text colours, never the series colour.

export const C = {
  leads: '#2a78d6', converted: '#eb6834', past: '#a9c9ef',
  grid: '#ebe9f0', axis: '#c9c6d3', ink: '#1f1b2d', ink2: '#57536a', muted: '#8a879a', track: '#f1eff6',
  gold: '#F0B429', goldTrack: 'rgba(240,180,41,0.22)',
};
export const nf = (n: number) => n.toLocaleString('en-IN');
export const dayLabel = (d: string) => new Date(`${d}T00:00:00Z`).toLocaleDateString('en-IN', { timeZone: 'UTC', day: 'numeric', month: 'short' });
export const weekdayLabel = (d: string) => new Date(`${d}T00:00:00Z`).toLocaleDateString('en-IN', { timeZone: 'UTC', weekday: 'short' });
// The server counts a lead with no source / course / reason under '—'
export const named = (s: string) => (!s || s === '—' ? 'Not given' : s);

// Width of a box, for charts drawn at their real pixel size
export function useWidth<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [w, setW] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => setW(Math.floor(e.contentRect.width)));
    ro.observe(el);
    setW(el.clientWidth);
    return () => ro.disconnect();
  }, []);
  return [ref, w] as const;
}

export function niceMax(v: number) {
  if (v <= 4) return 4;
  const p = Math.pow(10, Math.floor(Math.log10(v)));
  for (const m of [1, 2, 2.5, 5, 10]) if (v <= m * p) return m * p;
  return 10 * p;
}

// A bar with a 4px rounded data end and a square baseline
export function barPath(x: number, y: number, w: number, h: number, horizontal = false) {
  if (w <= 0 || h <= 0) return '';
  if (horizontal) {
    const r = Math.min(4, h / 2, w);
    return `M${x},${y} H${x + w - r} Q${x + w},${y} ${x + w},${y + r} V${y + h - r} Q${x + w},${y + h} ${x + w - r},${y + h} H${x} Z`;
  }
  const r = Math.min(4, w / 2, h);
  return `M${x},${y + h} V${y + r} Q${x},${y} ${x + r},${y} H${x + w - r} Q${x + w},${y} ${x + w},${y + r} V${y + h} Z`;
}

export const Card: React.FC<{ title: string; sub?: string; right?: React.ReactNode; children: React.ReactNode; className?: string }> = ({ title, sub, right, children, className = '' }) => (
  <div className={`bg-white rounded-2xl border border-gray-100 p-4 sm:p-5 min-w-0 ${className}`}>
    <div className="flex items-start justify-between gap-3 mb-3">
      <div className="min-w-0">
        <h2 className="font-bold text-gray-800">{title}</h2>
        {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
      </div>
      {right}
    </div>
    {children}
  </div>
);

export const Tile: React.FC<{ label: string; value: string; sub?: string; delta?: { text: string; good: boolean | null } }> = ({ label, value, sub, delta }) => (
  <div className="bg-white rounded-2xl border border-gray-100 px-4 py-3 min-w-0">
    <p className="text-xs font-semibold text-gray-500">{label}</p>
    <p className="text-2xl font-bold mt-1" style={{ color: C.ink }}>{value}</p>
    {delta && (
      <p className={`text-[11px] font-bold mt-0.5 ${delta.good === null ? 'text-gray-400' : delta.good ? 'text-green-700' : 'text-red-600'}`}>{delta.text}</p>
    )}
    {sub && <p className="text-[11px] text-gray-400 mt-0.5">{sub}</p>}
  </div>
);

// "▲ 3 vs last week" — up is good for every number on these pages
export function deltaOf(now: number, before: number, unit = '', period = 'last week'): { text: string; good: boolean | null } {
  const d = Math.round((now - before) * 10) / 10;
  if (d === 0) return { text: `= same as ${period}`, good: null };
  return { text: `${d > 0 ? '▲' : '▼'} ${Math.abs(d)}${unit} vs ${period}`, good: d > 0 };
}

// Counted reasons: one series, the count at each bar's tip
export const ReasonBars: React.FC<{ rows: { reason: string; n: number }[]; empty: string }> = ({ rows, empty }) => {
  if (!rows.length) return <p className="text-sm text-gray-400">{empty}</p>;
  const top = Math.max(...rows.map(r => r.n));
  return (
    <ul className="space-y-2">
      {rows.slice(0, 8).map(r => (
        <li key={r.reason} className="text-sm">
          <div className="flex justify-between gap-2"><span className="text-gray-700 truncate">{named(r.reason)}</span><b className="text-gray-900 tabular-nums">{nf(r.n)}</b></div>
          <div className="h-2 rounded-r mt-1" style={{ background: C.track }}>
            <div className="h-2 rounded-r" style={{ width: `${Math.max(3, (r.n / top) * 100)}%`, background: C.leads }} />
          </div>
        </li>
      ))}
    </ul>
  );
};

// One series in columns. The highlighted column (this week, today) is the
// full blue with its value on the cap; the others are a lighter step.
// Hover or focus a column for its numbers; "Show table" lists them all.
export interface ColumnDatum { key: string; label: string; value: number; tip: string }
export const Columns: React.FC<{ data: ColumnDatum[]; highlight?: number; fmt?: (v: number) => string; cap?: number; ariaLabel: string; tableHead: [string, string] }> = ({ data, highlight = data.length - 1, fmt = nf, cap, ariaLabel, tableHead }) => {
  const [ref, w] = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);
  const [table, setTable] = useState(false);
  const H = 190, pad = { l: 34, r: 8, t: 22, b: 24 };
  const plotW = Math.max(10, w - pad.l - pad.r), plotH = H - pad.t - pad.b;
  let max = niceMax(Math.max(1, ...data.map(d => d.value)));
  if (cap) max = Math.min(cap, max);
  const ticks = [0, max / 2, max];
  const band = plotW / Math.max(1, data.length);
  const barW = Math.max(4, Math.min(24, band * 0.56));
  const yOf = (v: number) => pad.t + plotH - (Math.min(v, max) / max) * plotH;
  const tip = hover !== null ? data[hover] : null;
  const tipLeft = hover !== null ? Math.min(Math.max(pad.l + hover * band + band / 2 - 80, 0), Math.max(0, w - 170)) : 0;
  const toggle = <button onClick={() => setTable(t => !t)} className="text-xs text-purple-600 font-semibold">{table ? 'Show chart' : 'Show table'}</button>;
  if (table) {
    return (
      <div className="space-y-2">
        <div className="flex justify-end">{toggle}</div>
        <div className="overflow-x-auto max-h-72 border border-gray-100 rounded-xl">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 sticky top-0"><tr>
              <th className="text-left px-3 py-2 text-xs font-bold text-gray-500">{tableHead[0]}</th>
              <th className="text-right px-3 py-2 text-xs font-bold text-gray-500">{tableHead[1]}</th>
            </tr></thead>
            <tbody>{data.map(d => (
              <tr key={d.key} className="border-t border-gray-100">
                <td className="px-3 py-1.5 text-gray-700">{d.label}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{d.tip}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      </div>
    );
  }
  return (
    <div className="space-y-2">
      <div className="flex justify-end">{toggle}</div>
      <div ref={ref} className="relative w-full" onMouseLeave={() => setHover(null)}>
        {w > 0 && (
          <svg width={w} height={H} role="img" aria-label={ariaLabel}>
            {ticks.map(t => (
              <g key={t}>
                <line x1={pad.l} x2={w - pad.r} y1={yOf(t)} y2={yOf(t)} stroke={t === 0 ? C.axis : C.grid} strokeWidth={1} />
                <text x={pad.l - 6} y={yOf(t)} textAnchor="end" dominantBaseline="middle" fontSize="11" fill={C.muted} style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(t)}</text>
              </g>
            ))}
            {data.map((d, i) => {
              const x = pad.l + i * band + (band - barW) / 2;
              const on = i === highlight;
              const showValue = on || hover === i;
              return (
                <g key={d.key}>
                  <path d={barPath(x, yOf(d.value), barW, pad.t + plotH - yOf(d.value))} fill={on ? C.leads : C.past} />
                  {showValue && <text x={x + barW / 2} y={yOf(d.value) - 6} textAnchor="middle" fontSize="11" fontWeight="700" fill={C.ink}>{fmt(d.value)}</text>}
                  <text x={pad.l + i * band + band / 2} y={H - 7} textAnchor="middle" fontSize="11" fill={on ? C.ink : C.muted} fontWeight={on ? 700 : 400}>{d.label}</text>
                  <rect x={pad.l + i * band} y={pad.t} width={band} height={plotH} fill="transparent" tabIndex={0} aria-label={`${d.label}: ${d.tip}`}
                    onMouseEnter={() => setHover(i)} onFocus={() => setHover(i)} onBlur={() => setHover(null)} style={{ outline: 'none' }} />
                </g>
              );
            })}
          </svg>
        )}
        {tip && (
          <div className="absolute top-0 pointer-events-none bg-white border border-gray-200 shadow-lg rounded-xl px-3 py-2 text-xs w-[170px]" style={{ left: tipLeft }}>
            <p className="text-gray-500 mb-0.5">{tip.label}</p>
            <p className="font-bold text-gray-900">{tip.tip}</p>
          </div>
        )}
      </div>
    </div>
  );
};

// A ring meter: the filled arc grows in on first show
export const Ring: React.FC<{ pct: number; size?: number; stroke?: number; color: string; track: string; label: string; children: React.ReactNode }> = ({ pct, size = 172, stroke = 14, color, track, label, children }) => {
  const [shown, setShown] = useState(0);
  useEffect(() => { const t = setTimeout(() => setShown(Math.max(0, Math.min(100, pct))), 60); return () => clearTimeout(t); }, [pct]);
  const r = (size - stroke) / 2, circ = 2 * Math.PI * r, dash = (shown / 100) * circ;
  return (
    <div className="relative flex-shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} role="img" aria-label={label}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={track} strokeWidth={stroke} />
        {dash > 0 && (
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke} strokeLinecap="round"
            strokeDasharray={`${dash} ${circ}`} transform={`rotate(-90 ${size / 2} ${size / 2})`} style={{ transition: 'stroke-dasharray .9s ease' }} />
        )}
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">{children}</div>
    </div>
  );
};

// Level badges (Starter → Diamond)
export const LEVEL_TINT: Record<Level['key'], { bg: string; fg: string }> = {
  starter: { bg: '#ecfdf5', fg: '#047857' },
  bronze:  { bg: '#fff7ed', fg: '#9a3412' },
  silver:  { bg: '#f1f5f9', fg: '#334155' },
  gold:    { bg: '#fefce8', fg: '#854d0e' },
  diamond: { bg: '#eff6ff', fg: '#1d4ed8' },
};
export const LevelBadge: React.FC<{ level: Level; small?: boolean }> = ({ level, small }) => {
  const t = LEVEL_TINT[level.key] || LEVEL_TINT.starter;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full font-bold whitespace-nowrap ${small ? 'text-[10px] px-2 py-0.5' : 'text-xs px-2.5 py-1'}`} style={{ background: t.bg, color: t.fg }}>
      <span aria-hidden>{level.emoji}</span>{level.name}
    </span>
  );
};

// India-time greeting
export function greeting() {
  const h = Number(new Date().toLocaleString('en-GB', { timeZone: 'Asia/Kolkata', hour: '2-digit', hour12: false }));
  return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
}
